/**
 * QC (Quality Control) API
 *
 * Handles QC result retrieval, override, and report download
 */
import helper from './helper'

export default {
  /**
   * Get a single QC result by ID
   * @param {number} id - QC result ID
   * @returns {Promise<Object>} QC result with parsed JSON fields
   */
  async getResult(id) {
    return await helper.get(`/api/qc/results/${id}`)
  },

  /**
   * List QC results with optional filtering
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (pending, queued, processing, passed, failed, etc.)
   * @param {number} params.episodeId - Filter by episode ID
   * @param {number} params.mediaId - Filter by media ID
   * @param {number} params.limit - Results per page (default 50, max 100)
   * @param {number} params.offset - Pagination offset
   * @returns {Promise<Object>} { results, total, limit, offset }
   */
  async list(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.episodeId) queryParams.append('episodeId', params.episodeId)
    if (params.mediaId) queryParams.append('mediaId', params.mediaId)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.offset) queryParams.append('offset', params.offset)

    const queryString = queryParams.toString()
    return await helper.get(`/api/qc/results${queryString ? '?' + queryString : ''}`)
  },

  /**
   * Override a QC result (mark as accepted despite warnings/failures)
   * @param {number} id - QC result ID
   * @param {string} reason - Reason for override (min 10 characters)
   * @returns {Promise<Object>} { success, id, previousStatus, newStatus, overriddenBy, reason }
   */
  async override(id, reason) {
    return await helper.post(`/api/qc/results/${id}/override`, { reason })
  },

  /**
   * Retry a failed QC job
   * @param {number} id - QC result ID
   * @param {number} storageConfigId - Optional storage config ID for S3-compatible storage
   * @returns {Promise<Object>} { success, id, status }
   */
  async retry(id, storageConfigId = null) {
    return await helper.post(`/api/qc/results/${id}/retry`, { storageConfigId })
  },

  /**
   * Get QC statistics
   * @returns {Promise<Object>} { statusCounts, last24Hours, loudness }
   */
  async getStats() {
    return await helper.get('/api/qc/stats')
  },

  /**
   * Get QC report download URL
   * @param {number} id - QC result ID
   * @returns {string} URL to download PDF report
   */
  getReportUrl(id) {
    return `/api/qc/results/${id}/report`
  },

  /**
   * Create a new QC job
   * @param {Object} params - Job parameters
   * @param {number} params.episodeId - Episode ID (optional)
   * @param {number} params.mediaId - Media ID (optional)
   * @param {string} params.sourcePath - S3 path to source file
   * @param {number} params.storageConfigId - Storage config ID for credentials
   * @param {number} params.expectedDurationMs - Expected duration in milliseconds
   * @returns {Promise<Object>} { success, qcResultId, status }
   */
  async createJob(params) {
    return await helper.post('/api/qc/jobs', params)
  }
}
